import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

const TEAL   = "FF008080";
const YELLOW = "FFFFF176";
const GREEN  = "FF016466";
const RED    = "FFD32F2F";
const WHITE  = "FFFFFFFF";
const BLACK  = "FF000000";
const LGRAY  = "FFF5F5F5";
const BGRAY  = "FFCCCCCC";

const solidFill  = (argb) => ({ type: "pattern", pattern: "solid", fgColor: { argb } });
const thinBorder = (argb = BGRAY) => ({ style: "thin", color: { argb } });
const allBorders = (argb = BGRAY) => ({ top: thinBorder(argb), bottom: thinBorder(argb), left: thinBorder(argb), right: thinBorder(argb) });

// Render a Chart.js config onto an offscreen canvas and return a PNG base64 string
function renderChartToPng(config, width = 900, height = 400) {
    return new Promise((resolve) => {
        const canvas = document.createElement("canvas");
        canvas.width  = width;
        canvas.height = height;
        canvas.style.position = "absolute";
        canvas.style.left = "-9999px";
        document.body.appendChild(canvas);

        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const chart = new Chart(canvas, {
            ...config,
            options: {
                ...config.options,
                animation: false,
                responsive: false,
            },
        });

        const png = canvas.toDataURL("image/png").split(",")[1];
        chart.destroy();
        document.body.removeChild(canvas);
        resolve(png);
    });
}

// UK score per associate — one bar per associate, deduplicates for "all" mode
function buildHorizontalBarConfig(formattedData, mode) {
    let entries;

    if (mode === "average") {
        // Already one row per associate — just map directly
        const seenNames = new Set();
        entries = formattedData
            .filter((r) => r["Ast ID"] && r["Associate Name"]?.trim())
            .map((r) => ({ name: r["Associate Name"].trim(), score: r["UK Score"] || 0 }))
            .filter((e) => {
                if (!e.name || seenNames.has(e.name)) return false;
                seenNames.add(e.name);
                return true;
            })
            .sort((a, b) => b.score - a.score);
    } else {
        // Deduplicate by Ast ID — build a name map first (best name per associate)
        const nameMap = {};
        formattedData.forEach((r) => {
            const id = r["Ast ID"];
            if (!id) return;
            const name = r["Associate Name"] && r["Associate Name"].trim();
            if (name && !nameMap[id]) nameMap[id] = name;
        });

        // Pick latest PM-submitted row per associate for the score
        const latestMap = {};
        formattedData.forEach((r) => {
            const id = r["Ast ID"];
            if (!id || !nameMap[id]) return;
            const existing = latestMap[id];
            const rEnd = r["_sortStart"] || r["_sortEnd"] || "";
            const exEnd = existing ? (existing["_sortStart"] || existing["_sortEnd"] || "") : "";
            if (!existing || rEnd > exEnd ||
                (rEnd === exEnd && r["PM Final Rating"] === "Yes" && existing["PM Final Rating"] !== "Yes") ||
                (rEnd === exEnd && r["PM Final Rating"] === existing["PM Final Rating"] && r["UK Score"] > existing["UK Score"])) {
                latestMap[id] = r;
            }
        });

        const seenNames = new Set();
        entries = Object.values(latestMap)
            .filter((r) => nameMap[r["Ast ID"]])
            .map((r) => ({ name: nameMap[r["Ast ID"]], score: r["UK Score"] || 0 }))
            .filter((e) => {
                if (!e.name || seenNames.has(e.name)) return false;
                seenNames.add(e.name);
                return true;
            })
            .sort((a, b) => b.score - a.score);
    }

    const labels = entries.map((e) => e.name);
    const scores = entries.map((e) => e.score);
    const colors = scores.map((s) => s >= 700 ? "rgba(0,128,128,0.85)" : s >= 500 ? "rgba(251,140,0,0.85)" : "rgba(229,57,53,0.85)");

    return {
        type: "bar",
        data: {
            labels,
            datasets: [{ label: "UK Score", data: scores, backgroundColor: colors, borderWidth: 0, barThickness: 14, categoryPercentage: 0.6, barPercentage: 0.6 }],
        },
        options: {
            indexAxis: "y",
            plugins: {
                title: { display: true, text: mode === "average" ? "Average UK Score by Associate" : "UK Score by Associate (Latest Period)", font: { size: 14, weight: "bold" } },
                legend: {
                    display: true,
                    position: "top",
                    labels: {
                        generateLabels: () => [
                            { text: "≥ 700  High",   fillStyle: "rgba(0,128,128,0.85)",   strokeStyle: "#008080", lineWidth: 1 },
                            { text: "500–699  Medium", fillStyle: "rgba(251,140,0,0.85)", strokeStyle: "#FB8C00", lineWidth: 1 },
                            { text: "< 500  Low",    fillStyle: "rgba(229,57,53,0.85)",  strokeStyle: "#E53935", lineWidth: 1 },
                        ],
                        font: { size: 11 },
                    },
                },
                datalabels: false,
            },
            scales: {
                x: { beginAtZero: false, min: 400, title: { display: true, text: "UK Score" } },
                y: { ticks: { font: { size: 10 }, autoSkip: false } },
            },
            datasets: { bar: { barThickness: 14, maxBarThickness: 16, categoryPercentage: 0.6, barPercentage: 0.6 } },
            animation: false,
            layout: { padding: { right: 50 } },
        },
        plugins: [{
            id: "barValueLabels",
            afterDatasetsDraw(chart) {
                const { ctx } = chart;
                chart.data.datasets.forEach((dataset, i) => {
                    const meta = chart.getDatasetMeta(i);
                    meta.data.forEach((bar, idx) => {
                        const value = dataset.data[idx];
                        if (!value) return;
                        ctx.save();
                        ctx.font = "bold 10px Arial";
                        ctx.fillStyle = "#333333";
                        ctx.textAlign = "left";
                        ctx.textBaseline = "middle";
                        ctx.fillText(value, bar.x + 4, bar.y);
                        ctx.restore();
                    });
                });
            },
        }],
    };
}

// Donut chart — submission status for all/latest mode, score distribution for average mode
function buildDonutConfig(formattedData, mode) {
    if (mode === "average") {
        // Score band distribution: High / Medium / Low based on averaged UK scores
        const records = formattedData.filter((r) => r["Ast ID"]);
        const high   = records.filter((r) => (r["UK Score"] || 0) >= 700).length;
        const medium = records.filter((r) => (r["UK Score"] || 0) >= 500 && (r["UK Score"] || 0) < 700).length;
        const low    = records.filter((r) => (r["UK Score"] || 0) > 0 && (r["UK Score"] || 0) < 500).length;
        const none   = records.filter((r) => !(r["UK Score"] || 0)).length;
        const total  = records.length;
        const pct = (n) => total > 0 ? `${Math.round((n / total) * 100)}%` : "0%";

        return {
            type: "doughnut",
            data: {
                labels: [
                    `High ≥700 (${pct(high)})`,
                    `Medium 500–699 (${pct(medium)})`,
                    `Low <500 (${pct(low)})`,
                    `No Score (${pct(none)})`,
                ],
                datasets: [{
                    data: [high, medium, low, none],
                    backgroundColor: ["rgba(0,128,128,0.85)", "rgba(251,140,0,0.85)", "rgba(229,57,53,0.85)", "rgba(189,189,189,0.85)"],
                    borderWidth: 2,
                    borderColor: ["#008080", "#FB8C00", "#E53935", "#9E9E9E"],
                }],
            },
            options: {
                plugins: {
                    title: { display: true, text: "Average Score Distribution", font: { size: 14, weight: "bold" } },
                    legend: { display: true, position: "bottom", labels: { font: { size: 11 } } },
                },
            },
        };
    }

    // all / latest mode — submission status breakdown based on latest period per associate
    const latestByAssoc = {};
    formattedData.forEach((r) => {
        const id = r["Ast ID"];
        if (!id) return;
        const existing = latestByAssoc[id];
        const rEnd = r["_sortStart"] || r["_sortEnd"] || "";
        const exEnd = existing ? (existing["_sortStart"] || existing["_sortEnd"] || "") : "";
        if (!existing || rEnd > exEnd ||
            (rEnd === exEnd && r["PM Final Rating"] === "Yes" && existing["PM Final Rating"] !== "Yes") ||
            (rEnd === exEnd && r["PM Final Rating"] === existing["PM Final Rating"] && r["UK Score"] > existing["UK Score"])) {
            latestByAssoc[id] = r;
        }
    });
    const records = Object.values(latestByAssoc).filter((r) => r["Ast ID"]);
    const pmSubmitted   = records.filter((r) => r["PM Final Rating"] === "Yes").length;
    const selfOnly      = records.filter((r) => r["Self Rating"] === "Yes" && r["PM Final Rating"] === "No").length;
    const notSubmitted  = records.filter((r) => r["Self Rating"] === "No" && r["PM Final Rating"] === "No").length;
    const total = records.length;

    const pct = (n) => total > 0 ? `${Math.round((n / total) * 100)}%` : "0%";

    return {
        type: "doughnut",
        data: {
            labels: [
                `PM Submitted (${pct(pmSubmitted)})`,
                `Self Only (${pct(selfOnly)})`,
                `Not Submitted (${pct(notSubmitted)})`,
            ],
            datasets: [{
                data: [pmSubmitted, selfOnly, notSubmitted],
                backgroundColor: ["rgba(0,128,128,0.85)", "rgba(251,140,0,0.85)", "rgba(229,57,53,0.85)"],
                borderWidth: 2,
                borderColor: ["#008080", "#FB8C00", "#E53935"],
            }],
        },
        options: {
            plugins: {
                title: { display: true, text: "Submission Status Breakdown", font: { size: 14, weight: "bold" } },
                legend: { display: true, position: "bottom", labels: { font: { size: 11 } } },
            },
        },
    };
}

export async function exportBiWeeklyRatingsExcel({ formattedData, mode, startDate, endDate }) {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "EmPortal";
    workbook.created = new Date();

    const sheet = workbook.addWorksheet("Ratings", {
        views: [{ state: "frozen", ySplit: 1 }],
    });

    const isAverage = mode === "average";

    const headers = isAverage
        ? ["Ast ID", "Associate Name", "Project", "Project Manager", "Date Range", "Avg UK Score"]
        : ["Ast ID", "Associate Name", "Project", "Project Manager", "Date Range", "UK Score", "Self Rating", "PM Final Rating", "Combined Status"];

    sheet.columns = isAverage
        ? [
            { key: "Ast ID",          width: 10 },
            { key: "Associate Name",  width: 30 },
            { key: "Project",         width: 32 },
            { key: "Project Manager", width: 26 },
            { key: "Date Range",      width: 26 },
            { key: "Avg UK Score",    width: 14 },
          ]
        : [
            { key: "Ast ID",          width: 10 },
            { key: "Associate Name",  width: 30 },
            { key: "Project",         width: 32 },
            { key: "Project Manager", width: 26 },
            { key: "Date Range",      width: 26 },
            { key: "UK Score",        width: 12 },
            { key: "Self Rating",     width: 15 },
            { key: "PM Final Rating", width: 17 },
            { key: "Combined Status", width: 16 },
          ];

    // Header row
    const headerRow = sheet.addRow(headers);
    headerRow.height = 22;
    headerRow.eachCell((cell) => {
        cell.font      = { bold: true, color: { argb: WHITE }, size: 11 };
        cell.fill      = solidFill(TEAL);
        cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
        cell.border    = allBorders(BLACK);
    });

    const yesNoCols = new Set(["Self Rating", "PM Final Rating", "Combined Status"]);

    formattedData.forEach((rowData, rIdx) => {
        // Map "UK Score" → "Avg UK Score" for average mode
        const rowValues = isAverage
            ? headers.map((h) => h === "Avg UK Score" ? (rowData["UK Score"] ?? "") : (rowData[h] ?? ""))
            : headers.map((h) => rowData[h] ?? "");

        const row = sheet.addRow(rowValues);
        row.height = 18;
        const isEven = rIdx % 2 === 0;

        row.eachCell((cell, colNumber) => {
            const colName    = headers[colNumber - 1];
            const isStatusCol = yesNoCols.has(colName);
            const isUkCol    = colName === "UK Score" || colName === "Avg UK Score";
            const isCarried  = isUkCol && rowData["_carriedUkScore"];
            const val        = cell.value;

            if (isStatusCol) {
                const isYes = val === "Yes";
                cell.font      = { bold: true, color: { argb: WHITE }, size: 10 };
                cell.fill      = solidFill(isYes ? GREEN : RED);
                cell.alignment = { horizontal: "center", vertical: "middle" };
            } else if (isAverage && isUkCol) {
                // Colour-code avg score: green/yellow/red bands
                const score = val || 0;
                const bandColor = score >= 700 ? "FF016466" : score >= 500 ? "FFFF8F00" : score > 0 ? RED : BGRAY;
                cell.font      = { bold: true, color: { argb: WHITE }, size: 10 };
                cell.fill      = solidFill(bandColor);
                cell.alignment = { horizontal: "right", vertical: "middle" };
            } else if (isCarried) {
                cell.font      = { bold: true, color: { argb: BLACK }, size: 10 };
                cell.fill      = solidFill(YELLOW);
                cell.alignment = { horizontal: "right", vertical: "middle" };
            } else {
                cell.font      = { color: { argb: BLACK }, size: 10 };
                cell.fill      = solidFill(isEven ? LGRAY : WHITE);
                cell.alignment = {
                    horizontal: colNumber === 1 || typeof val === "number" ? "right" : "left",
                    vertical: "middle",
                };
            }
            cell.border = allBorders();
        });
    });

    // ── Charts: horizontal bar (left) + donut (right) side by side ───────────
    const dataRowCount = formattedData.length;
    const chartStartRow = dataRowCount + 3; // 2 blank rows gap

    // Each unique associate = one bar row (~20px each), min 400, max 1000
    const uniqueAssociates = Object.keys(
        formattedData.reduce((acc, r) => { if (r["Ast ID"] && r["Associate Name"]?.trim()) acc[r["Ast ID"]] = 1; return acc; }, {})
    ).length;
    const barHeight = Math.max(400, Math.min(uniqueAssociates * 30 + 140, 1400));
    const donutSize = 420;

    const [barPng, donutPng] = await Promise.all([
        renderChartToPng(buildHorizontalBarConfig(formattedData, mode), 720, barHeight),
        renderChartToPng(buildDonutConfig(formattedData, mode), donutSize, donutSize),
    ]);

    // Bar: cols A–F (0–5), Donut: cols G–I (6–8) — each placed independently
    const barRows  = Math.round(barHeight / 20) + 1;
    const donutRows = Math.round(donutSize / 20) + 1;

    sheet.addImage(workbook.addImage({ base64: barPng, extension: "png" }), {
        tl: { col: 0, row: chartStartRow },
        ext: { width: 720, height: barHeight },
    });

    sheet.addImage(workbook.addImage({ base64: donutPng, extension: "png" }), {
        tl: { col: 6, row: chartStartRow },
        ext: { width: donutSize, height: donutSize },
    });

    // ── Save ─────────────────────────────────────────────────────────────────
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
        new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
        `AssociateRatings_${moment(startDate).format("DD-MM-YYYY")}_to_${moment(endDate).format("DD-MM-YYYY")}.xlsx`
    );
}
